import { Wallet, CheckCircle, Clock } from "lucide-react";
import { money, fmtDay } from "../lib/payments";

export const RecruiterPayments = ({ payments }) => {
  if (!payments) return null;
  const rows = [];
  for (const p of payments) {
    for (const part of ["setter_1", "setter_2"]) {
      const info = (p.parts || {})[part] || {};
      if (info.amount == null) continue;
      rows.push({
        key: `${p.id}-${part}`,
        driver: p.driver_name,
        half: part === "setter_1" ? "1st half (start day)" : "2nd half (two weeks later)",
        amount: info.amount,
        due: info.due_date,
        paid: info.paid_at,
      });
    }
  }
  const totalOwed = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const totalPaid = rows.filter((r) => r.paid).reduce((s, r) => s + (r.amount || 0), 0);
  const outstanding = totalOwed - totalPaid;

  return (
    <div data-testid="recruiter-payments-page" className="space-y-4">
      <div className="rounded-md border border-sky-700/40 bg-sky-950/20 px-4 py-3 text-xs font-mono text-sky-200">
        You're seeing only your own setter payouts. Carrier payments, revenue, and other recruiters' earnings stay with the admin.
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat testid="recruiter-stat-owed" label="Total owed to you" value={money(totalOwed)} />
        <Stat testid="recruiter-stat-paid" label="Already paid" value={money(totalPaid)} />
        <Stat testid="recruiter-stat-outstanding" label="Still outstanding" value={money(outstanding)} />
      </div>

      {rows.length === 0 ? (
        <div data-testid="recruiter-payments-empty" className="py-16 text-center border border-dashed border-[#262C38] rounded-md">
          <Wallet className="h-6 w-6 mx-auto text-slate-600 mb-2" />
          <p className="text-slate-400 text-sm">No setter payouts yet.</p>
          <p className="text-slate-600 text-xs font-mono mt-1">Payouts appear when a driver you set is marked Hired by the admin.</p>
        </div>
      ) : (
        <div className="bg-[#13161C] border border-[#262C38] rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-3 py-2 text-left">Driver</th>
                <th className="px-3 py-2 text-left">Half</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right">Due</th>
                <th className="px-3 py-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-testid={`recruiter-payout-row-${r.key}`} className="border-t border-[#262C38]">
                  <td className="px-3 py-2.5 text-white">{r.driver}</td>
                  <td className="px-3 py-2.5 text-slate-300 text-xs font-mono">{r.half}</td>
                  <td className="px-3 py-2.5 text-right text-white font-mono font-bold">{money(r.amount)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-400 font-mono text-xs">{fmtDay(r.due)}</td>
                  <td className="px-3 py-2.5 text-right">
                    {r.paid ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-mono">
                        <CheckCircle className="h-3.5 w-3.5" /> Paid {fmtDay(r.paid)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-300 text-xs font-mono">
                        <Clock className="h-3.5 w-3.5" /> Pending
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-[#13161C] border border-[#262C38] rounded-md px-4 py-3">
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="font-heading text-xl font-bold text-white mt-1">{value}</p>
  </div>
);
